import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Modal,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import languagesData from "../assets/languages.json";

function StrategyDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [creators, setCreators] = useState([
    { type: "", count: "", category: "", location: "" },
  ]);
  const [campaign, setCampaign] = useState({});
  const [readOnlyFields, setReadOnlyFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [formData, setFormData] = useState({
    team: "dm_team",
    campaignGoal: "",
    campaignType: "",
    primaryKPIs: "",
    secondaryKPIs: "",
    influencerCost: "",
    others: "",
    occupation: "",
    interest: "",
    instagramReel: "",
    instagramStory: "",
    instagramDigitalRights: "",
    youtubeShorts: "",
    youtubeVideo: "",
    youtubeDigitalRights: "",
    customerInsights: "",
    campaignMessage: "",
    problemSolves: "",
    contentIdeas: "",
    openingHooks: "",
    callToAction: "",
    campaign: "",
    languages: "",
    campaignBudget: "",
    location: "",
    gender: "",
    age: "",
  });

  useEffect(() => {
    if (id) fetchCampaignDetails(id);
    fetchStates("India");
    fetchLanguages();
  }, [id]);

  useEffect(() => {
    if (campaign && campaign.dmTeam) {
      const updatedData = {
        campaignGoal: campaign.campaignName || "",
        campaignType: campaign.dmTeam?.campaignType || "",
        primaryKPIs: campaign.dmTeam?.primaryKpis || "",
        secondaryKPIs: campaign.dmTeam?.secondaryKpis || "",
        influencerCost: campaign.dmTeam?.influencerCost || "",
        others: campaign.dmTeam?.others || "",
        occupation: campaign.dmTeam?.occupation || "",
        interest: campaign.dmTeam?.interest || "",
        instagramReel: campaign.dmTeam?.instagram?.reels || "",
        instagramStory: campaign.dmTeam?.instagram?.stories || "",
        instagramDigitalRights: campaign.dmTeam?.instagram?.digitalRights || "",
        youtubeShorts: campaign.dmTeam?.youtube?.shorts || "",
        youtubeVideo: campaign.dmTeam?.youtube?.videos || "",
        youtubeDigitalRights: campaign.dmTeam?.youtube?.digitalRights || "",
        customerInsights: campaign.dmTeam?.customerInsights || "",
        campaignMessage: campaign.dmTeam?.campaignMessage || "",
        problemSolves: campaign.dmTeam?.problemSolves || "",
        contentIdeas: campaign.dmTeam?.contentIdeas || "",
        openingHooks: campaign.dmTeam?.openingHooks || "",
        callToAction: campaign.dmTeam?.callToAction || "",
        campaign: campaign.campaignName || "",
        languages: campaign.brandTeam?.languages || "",
        campaignBudget: campaign.brandTeam?.budget || "",
        location: campaign.brandTeam?.market || "",
        gender: campaign.brandTeam?.gender || "",
        age: campaign.brandTeam?.age || "",
      };
      setFormData(updatedData);

      if (campaign.dmTeam?.creators && campaign.dmTeam.creators.length > 0) {
        setCreators(campaign.dmTeam.creators.map(c => ({
          type: c.typeOfCreator || "",
          count: c.numberOfCreators || "",
          category: c.creatorCategory || "",
          location: c.creatorLocation || ""
        })));
      }

      const readOnlyList = ["campaignGoal", "campaign", "languages", "campaignBudget", "location", "gender", "age"];
      const filledFields = Object.keys(updatedData).filter(key => !readOnlyList.includes(key));
      setReadOnlyFields([...readOnlyList, ...filledFields]);
    } else if (campaign && campaign.brandTeam) {
      setFormData((prev) => ({
        ...prev,
        campaignGoal: campaign.campaignName || "",
        campaign: campaign.campaignName || "",
        languages: campaign.brandTeam?.languages || "",
        campaignBudget: campaign.brandTeam?.budget || "",
        location: campaign.brandTeam?.market || "",
        gender: campaign.brandTeam?.gender || "",
        age: campaign.brandTeam?.age || "",
      }));
      setReadOnlyFields(["campaignGoal", "campaign", "languages", "campaignBudget", "location", "gender", "age"]);
    }
  }, [campaign]);

  const isReadOnly = (fieldName) => readOnlyFields.includes(fieldName);

  const fetchLanguages = () => {
    try {
      setLanguages(languagesData.google_supported_languages || []);
    } catch (error) {
      console.error("Failed to fetch Languages:", error);
    }
  };

  const fetchCampaignDetails = async (id) => {
    try {
      const { default: api } = await import("../services/api");
      const response = await api.get(`/campaign/${id}`);
      setCampaign(response.data?.data || {});
    } catch (error) {
      console.error("Failed to fetch Campaign Details:", error);
    }
  };

  const fetchStates = async (country) => {
    try {
      const response = await axios.post(
        "https://countriesnow.space/api/v0.1/countries/states",
        { country },
      );
      setStates(response.data?.data?.states || []);
    } catch (error) {
      console.error("Failed to fetch States:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.campaignType)
      newErrors.campaignType = "Campaign type is required";
    if (!formData.primaryKPIs)
      newErrors.primaryKPIs = "Primary KPIs is required";

    // Validate creators
    creators.forEach((creator, index) => {
      if (!creator.type) {
        newErrors[`creator_${index}_type`] = "Creator type is required";
      }
      if (!creator.count) {
        newErrors[`creator_${index}_count`] = "Number of creators is required";
      }
    });

    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const { default: api } = await import("../services/api");
      const submitData = {
        ...formData,
        creators,
      };
      await api.put(`/campaign/flow/${id}`, submitData);
      fetchCampaignDetails(id);
      navigate('/marketing');
    } catch (error) {
      console.error("Failed to submit strategy details:", error);
    }
  };

  const handleCreatorChange = (index, field, value) => {
    const updatedCreators = [...creators];
    updatedCreators[index][field] = value;
    setCreators(updatedCreators);

    // Clear errors for this field
    if (errors[`creator_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`creator_${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const addCreatorRow = () => {
    setCreators([
      ...creators,
      { type: "", count: "", category: "", location: "" },
    ]);
  };

  const deleteCreatorRow = (index) => {
    if (creators.length > 1) {
      const updatedCreators = creators.filter((_, i) => i !== index);
      setCreators(updatedCreators);
    }
  };

  const handleReset = () => {
    setFormData({
      team: "dm_team",
      campaignGoal: campaign?.campaignName || "",
      campaignType: "",
      primaryKPIs: "",
      secondaryKPIs: "",
      influencerCost: "",
      others: "",
      occupation: "",
      interest: "",
      instagramReel: "",
      instagramStory: "",
      instagramDigitalRights: "",
      youtubeShorts: "",
      youtubeVideo: "",
      youtubeDigitalRights: "",
      customerInsights: "",
      campaignMessage: "",
      problemSolves: "",
      contentIdeas: "",
      openingHooks: "",
      callToAction: "",
      campaign: campaign?.campaignName || "",
      languages: campaign?.brandTeam?.languages || "",
      campaignBudget: campaign?.brandTeam?.budget || "",
      location: campaign?.brandTeam?.market || "",
      gender: campaign?.brandTeam?.gender || "",
      age: campaign?.brandTeam?.age || "",
    });
    setCreators([{ type: "", count: "", category: "", location: "" }]);
    setErrors({});
  };

  const handleApprove = async () => {
    try {
      const { default: api } = await import("../services/api");
      await api.put(`/campaign/flow/${id}`, { decision: "approve" });
      fetchCampaignDetails(id);
      navigate('/marketing');
    } catch (error) {
      console.error("Failed to approve:", error);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) return;
    try {
      const { default: api } = await import("../services/api");
      await api.put(`/campaign/flow/${id}`, { decision: "reject", reason: rejectReason });
      setOpenRejectModal(false);
      setRejectReason("");
      fetchCampaignDetails(id);
      navigate('/marketing');
    } catch (error) {
      console.error("Failed to reject:", error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
            Strategy Details 
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={'text.secondary'} sx={{ mb: 2}}>
              Campaign ID : <strong style={{ color: '#2e7d32' }}> {campaign?.campaignId ?  campaign?.campaignId : '-'} </strong>
            </Typography>
            <Typography variant="body2" color={'text.secondary'} sx={{ mb: 2}}>
              Campaign Name : <strong style={{ color: '#2e7d32' }}> {campaign?.campaignName} </strong>
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Campaign Expectation
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Campaign goal"
                size="small"
                value={formData.campaignGoal}
                onChange={(e) => handleChange("campaignGoal", e.target.value)}
                InputProps={{ readOnly: isReadOnly("campaignGoal") }}
                placeholder="Campaign goal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Campaign type"
                size="small"
                value={formData.campaignType}
                onChange={(e) => handleChange("campaignType", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("campaignType"),
                }}
                placeholder="Campaign type"
                error={!!errors.campaignType}
                helperText={errors.campaignType}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Primary KPIs"
                size="small"
                value={formData.primaryKPIs}
                onChange={(e) => handleChange("primaryKPIs", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("primaryKPIs"),
                }}
                placeholder="Primary KPIs"
                error={!!errors.primaryKPIs}
                helperText={errors.primaryKPIs}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Secondary KPIs"
                size="small"
                value={formData.secondaryKPIs}
                onChange={(e) => handleChange("secondaryKPIs", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("secondaryKPIs"),
                }}
                placeholder="Secondary KPIs"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Cost
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Campaign budget"
                size="small"
                value={formData.campaignBudget}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{ readOnly: isReadOnly("campaignBudget") }}
                onChange={(e) => handleChange("campaignBudget", e.target.value)}
                placeholder="Budget"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Influencer cost"
                size="small"
                value={formData.influencerCost}
                onChange={(e) => handleChange("influencerCost", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("influencerCost"),
                }}
                placeholder="Cost"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Others"
                size="small"
                value={formData.others}
                onChange={(e) => handleChange("others", e.target.value)}
                inputProps={{ readOnly: readOnlyFields.includes("others") }}
                placeholder="Others"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Customer
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location}
                  label="Location"
                  inputProps={{ readOnly: isReadOnly("location") }}
                  onChange={(e) => handleChange("location", e.target.value)}
                >
                  <MenuItem value="">Select Location</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.name} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  inputProps={{ readOnly: isReadOnly("gender") }}
                  label="Gender"
                  onChange={(e) => handleChange("gender", e.target.value)}
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem key={"male"} value="male">
                    Male
                  </MenuItem>
                  <MenuItem key={"female"} value="female">
                    Female
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Age"
                size="small"
                value={formData.age}
                InputProps={{ readOnly: isReadOnly("age") }}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                onChange={(e) => handleChange("age", e.target.value)}
                placeholder="Age"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Occupation"
                size="small"
                value={formData.occupation}
                onChange={(e) => handleChange("occupation", e.target.value)}
                inputProps={{ readOnly: readOnlyFields.includes("occupation") }}
                placeholder="Occupation"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="Interest"
                size="small"
                value={formData.interest}
                onChange={(e) => handleChange("interest", e.target.value)}
                inputProps={{ readOnly: readOnlyFields.includes("interest") }}
                placeholder="Interest"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Channel
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Instagram
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Reel"
                size="small"
                value={formData.instagramReel}
                onChange={(e) => handleChange("instagramReel", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("instagramReel"),
                }}
                placeholder="Reel"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Story"
                size="small"
                value={formData.instagramStory}
                onChange={(e) => handleChange("instagramStory", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("instagramStory"),
                }}
                placeholder="Story"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Digital rights"
                size="small"
                value={formData.instagramDigitalRights}
                onChange={(e) =>
                  handleChange("instagramDigitalRights", e.target.value)
                }
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("instagramDigitalRights"),
                }}
                placeholder="Digital rights"
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            YouTube
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Shorts"
                size="small"
                value={formData.youtubeShorts}
                onChange={(e) => handleChange("youtubeShorts", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("youtubeShorts"),
                }}
                placeholder="Shorts"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Video"
                size="small"
                value={formData.youtubeVideo}
                onChange={(e) => handleChange("youtubeVideo", e.target.value)}
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("youtubeVideo"),
                }}
                placeholder="Video"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Digital rights"
                size="small"
                value={formData.youtubeDigitalRights}
                onChange={(e) =>
                  handleChange("youtubeDigitalRights", e.target.value)
                }
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()}
                inputProps={{ className: "no-spinner" }}
                InputProps={{
                  readOnly: readOnlyFields.includes("youtubeDigitalRights"),
                }}
                placeholder="Digital rights"
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Creator
            </Typography>
            {!campaign.dmTeam && (
              <IconButton
                onClick={addCreatorRow}
                size="small"
                sx={{
                  bgcolor: "#019383",
                  color: "#1a1d29",
                  "&:hover": { bgcolor: "#1a1d29", color: "#019383" },
                }}
              >
                <Add />
              </IconButton>
            )}
          </Box>
          {creators.map((creator, index) => (
            <Grid
              container
              spacing={2}
              key={index}
              sx={{ mb: 2, alignItems: "center" }}
            >
              <Grid item xs={12} md={2.7}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors[`creator_${index}_type`]}
                >
                  <InputLabel>Type of creator</InputLabel>
                  <Select
                    value={creator.type}
                    label="Type of creator"
                    onChange={(e) =>
                      handleCreatorChange(index, "type", e.target.value)
                    }
                    inputProps={{ readOnly: !!campaign.dmTeam }}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem key={"all"} value="all">
                      All
                    </MenuItem>
                    <MenuItem key={"Micro"} value="Micro">
                      Micro: 10K-100K
                    </MenuItem>
                    <MenuItem key={"Macro"} value="Macro">
                      Macro: 100K-500K
                    </MenuItem>
                    <MenuItem key={"Mid-tier"} value="Mid-tier">
                      Mid-tier: 500K-1M
                    </MenuItem>
                    <MenuItem key={"Mega"} value="Mega">
                      Mega: 1M-5M
                    </MenuItem>
                    <MenuItem key={"Celeb"} value="Celeb">
                      Celeb: 5M+
                    </MenuItem>
                  </Select>
                  {errors[`creator_${index}_type`] && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors[`creator_${index}_type`]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.7}>
                <TextField
                  fullWidth
                  label="No of creators"
                  size="small"
                  value={creator.count}
                  onChange={(e) =>
                    handleCreatorChange(index, "count", e.target.value)
                  }
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ className: "no-spinner" }}
                  InputProps={{ readOnly: !!campaign.dmTeam }}
                  placeholder="No of creators"
                  error={!!errors[`creator_${index}_count`]}
                  helperText={errors[`creator_${index}_count`]}
                />
              </Grid>
              <Grid item xs={12} md={2.7}>
                <TextField
                  fullWidth
                  label="Creator category"
                  size="small"
                  value={creator.category}
                  onChange={(e) =>
                    handleCreatorChange(index, "category", e.target.value)
                  }
                  placeholder="Category"
                  InputProps={{ readOnly: !!campaign.dmTeam }}
                />
              </Grid>
              <Grid item xs={12} md={2.7}>
                <TextField
                  fullWidth
                  label="Creator location"
                  size="small"
                  value={creator.location}
                  onChange={(e) =>
                    handleCreatorChange(index, "location", e.target.value)
                  }
                  placeholder="Location"
                  InputProps={{ readOnly: !!campaign.dmTeam }}
                />
              </Grid>
              <Grid item xs={12} md={1.2}>
                {!campaign.dmTeam && (
                  <IconButton
                    onClick={() => deleteCreatorRow(index)}
                    size="small"
                    disabled={creators.length === 1}
                    sx={{
                      color: creators.length === 1 ? "#ccc" : "#f44336",
                      "&:hover": {
                        bgcolor:
                          creators.length === 1 ? "transparent" : "#ffebee",
                      },
                    }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Typography
            variant="subtitle1"
            sx={{ mb: 2, mt: 2, fontWeight: 600 }}
          >
            Customer Insights
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Customer Insights"
                size="small"
                multiline
                rows={4}
                value={formData.customerInsights}
                onChange={(e) =>
                  handleChange("customerInsights", e.target.value)
                }
                placeholder="Customer Insights"
                inputProps={{
                  readOnly: readOnlyFields.includes("customerInsights"),
                }}
              />
            </Grid>
          </Grid>

          <Typography
            variant="subtitle1"
            sx={{ mb: 2, mt: 2, fontWeight: 600 }}
          >
            Content
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Campaign"
                size="small"
                value={formData.campaign}
                InputProps={{ readOnly: isReadOnly("campaign") }}
                onChange={(e) => handleChange("campaign", e.target.value)}
                placeholder="Campaign"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Campaign message to carry"
                size="small"
                value={formData.campaignMessage}
                onChange={(e) =>
                  handleChange("campaignMessage", e.target.value)
                }
                inputProps={{
                  readOnly: readOnlyFields.includes("campaignMessage"),
                }}
                placeholder="Campaign message to carry"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Problem Solves"
                size="small"
                value={formData.problemSolves}
                onChange={(e) => handleChange("problemSolves", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("problemSolves"),
                }}
                placeholder="Problem Solves"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={formData.languages}
                  inputProps={{ readOnly: isReadOnly("languages") }}
                  onChange={(e) => handleChange("languages", e.target.value)}
                >
                  <MenuItem value="">Select Languages</MenuItem>
                  {languages.map((language) => (
                    <MenuItem key={language.code} value={language.name}>
                      {language.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Content Ideas"
                size="small"
                value={formData.contentIdeas}
                onChange={(e) => handleChange("contentIdeas", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("contentIdeas"),
                }}
                placeholder="Content Ideas"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Opening Hooks"
                size="small"
                value={formData.openingHooks}
                onChange={(e) => handleChange("openingHooks", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("openingHooks"),
                }}
                placeholder="Opening Hooks"
              />
            </Grid>
          </Grid>

          <Typography
            variant="subtitle1"
            sx={{ mb: 2, mt: 2, fontWeight: 600 }}
          >
            Call to Action
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Call To Action"
                size="small"
                value={formData.callToAction}
                onChange={(e) => handleChange("callToAction", e.target.value)}
                inputProps={{
                  readOnly: readOnlyFields.includes("callToAction"),
                }}
                placeholder="Call To Action"
              />
            </Grid>
          </Grid>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            {!campaign.dmTeam && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    px: 4,
                    py: 1,
                    bgcolor: "#1a1d29",
                    color: "#ffffff",
                    "&:hover": {
                      bgcolor: "#019383",
                      color: "#1a1d29"
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ px: 4, py: 1 }}
                >
                  Submit
                </Button>
              </>
            )}
          </Box>

          {campaign.stageStatus === 3 && user.role == 'brand_team' && campaign.status !== 2 && (
            <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Review Strategy
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  sx={{
                    px: 4,
                    py: 1,
                    bgcolor: "#019383",
                    color: "#1a1d29",
                    "&:hover": {
                      bgcolor: "#b0d060"
                    }
                  }}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setOpenRejectModal(true)}
                  sx={{
                    px: 4,
                    py: 1,
                    borderColor: "#f44336",
                    color: "#f44336",
                    "&:hover": {
                      borderColor: "#d32f2f",
                      bgcolor: "#ffebee"
                    }
                  }}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )}

          <Modal
            open={openRejectModal}
            onClose={() => setOpenRejectModal(false)}
          >
            <Box sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Reject Strategy
              </Typography>
              <TextField
                fullWidth
                label="Reason for rejection"
                multiline
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenRejectModal(false);
                    setRejectReason("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRejectSubmit}
                  disabled={!rejectReason.trim()}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Modal>
        </CardContent>
      </Card>
    </Box>
  );
}

export default StrategyDetails;
